"use client";
import { useEffect, useState, useRef, Suspense } from "react";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { ChevronLeft, Search, ShoppingBag } from "lucide-react";
import {
    getCategories,
    getSubCategories,
    subscribeToProducts
} from "@/firebase/firestoreServices";
import { where } from "firebase/firestore";
import ProductCard from "@/components/ProductCard";
import { SkeletonProduct } from "@/components/SkeletonLoaders";
import type { Category, SubCategory, Product } from "@/models";
import { useCart } from "@/context/CartContext";
import { useOutlet } from "@/context/OutletContext";
import { motion, AnimatePresence } from "framer-motion";
import LazyImage from "@/components/LazyImage";

const CategoryProductsContent = () => {
    const params = useParams();
    const categoryId = params?.id as string;
    const searchParams = useSearchParams();
    const [subParam, setSubParam] = useState<string | null>(null);

    useEffect(() => {
        if (searchParams) {
            setSubParam(searchParams.get("sub"));
        }
    }, [searchParams]);

    const navigate = useRouter();
    const { totalItems } = useCart();
    const { selectedOutlet } = useOutlet();

    const [category, setCategory] = useState<Category | null>(null);
    const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [selectedSubCatId, setSelectedSubCatId] = useState<string>(subParam ?? "all");
    const [loading, setLoading] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);

    const scrollRef = useRef<HTMLDivElement>(null);
    const subCategoriesRef = useRef<SubCategory[]>([]);
    useEffect(() => {
        subCategoriesRef.current = subCategories;
    }, [subCategories]);

    useEffect(() => {
        setCategory(null);
        setSubCategories([]);
        setProducts([]);
        setSelectedSubCatId(subParam ?? "all");
        setLoading(true);
        setLoadingProducts(true);
    }, [categoryId, subParam]);

    useEffect(() => {
        if (!categoryId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const cats = await getCategories();
                const currentCat = (cats as Category[]).find(c => c.id === categoryId);
                if (currentCat) setCategory(currentCat);

                const subs = await getSubCategories(categoryId);
                setSubCategories(subs as SubCategory[]);
            } catch (e) {
                console.error("Error fetching category data:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [categoryId, subParam]);

    useEffect(() => {
        if (!categoryId) return;
        if (selectedSubCatId === "all" && subCategories.length === 0) return;

        setLoadingProducts(true);
        const constraints = selectedSubCatId !== "all"
            ? [where("subcategories", "array-contains", selectedSubCatId)]
            : [];

        const unsub = subscribeToProducts(selectedOutlet?.id, constraints, (data) => {
            let filtered = data as Product[];
            if (selectedSubCatId === "all") {
                const subIds = subCategoriesRef.current.map(s => s.id);
                filtered = (data as Product[]).filter(p =>
                    p.subcategories?.some(sid => subIds.includes(sid))
                );
            }
            setProducts(filtered);
            setLoadingProducts(false);
        });

        return () => unsub();
    }, [categoryId, selectedSubCatId, selectedOutlet?.id, subCategories.length]);

    if (loading && !category) {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 pb-20">
            {/* Top Header */}
            <div className="flex items-center justify-between sticky top-0 bg-background/80 backdrop-blur-md z-10 py-2">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate.back()}
                        className="p-2 hover:bg-muted rounded-full transition-colors" name="navigate back"
                        title="Go Back"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <h1 className="text-xl font-bold">{category?.name || "Category"}</h1>
                </div>
            </div>

            {/* Subcategory Picker (Horizontal Scroll) */}
            <div
                ref={scrollRef}
                className="flex items-center gap-6 overflow-x-auto pb-4 no-scrollbar scroll-smooth"
            >
                <button
                    onClick={() => {
                        setSelectedSubCatId("all");
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex flex-col items-center gap-2 flex-shrink-0 group"
                >
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center border-2 transition-all ${selectedSubCatId === "all"
                        ? "border-primary bg-primary shadow-sm shadow-primary/30"
                        : "border-border bg-card group-hover:border-primary/50"
                        }`}>
                        <span className={`text-sm font-bold ${selectedSubCatId === "all" ? "text-white" : "text-muted-foreground"}`}>
                            All
                        </span>
                    </div>
                    <span className={`text-xs font-medium transition-colors ${selectedSubCatId === "all" ? "text-primary font-bold uppercase tracking-wider" : "text-muted-foreground"
                        }`}>
                        All
                    </span>
                </button>

                {subCategories.map((sub) => (
                    <button
                        key={sub.id}
                        onClick={() => {
                            setSelectedSubCatId(sub.id);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="flex flex-col items-center gap-2 flex-shrink-0 group"
                    >
                        <div className={`w-16 h-16 rounded-full overflow-hidden border-2 transition-all ${selectedSubCatId === sub.id
                            ? "border-primary bg-white shadow-md shadow-primary/20"
                            : "border-border bg-card group-hover:border-primary/50"
                            }`}>
                            {sub.imageUrl ? (
                                <LazyImage
                                    src={sub.imageUrl}
                                    alt={sub.name}
                                    className={`w-full h-full object-cover transition-transform duration-300 ${selectedSubCatId === sub.id ? "scale-110" : "group-hover:scale-105"
                                        }`}
                                    wrapperClassName="w-full h-full"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-lg font-bold text-muted-foreground">
                                    {sub.name[0]}
                                </div>
                            )}
                        </div>
                        <span className={`text-xs font-medium transition-colors whitespace-nowrap px-1 ${selectedSubCatId === sub.id ? "text-primary font-bold" : "text-muted-foreground"
                            }`}>
                            {sub.name}
                        </span>
                    </button>
                ))}
            </div>

            {/* Product List */}
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="font-bold text-foreground">
                        {selectedSubCatId === "all"
                            ? `All ${category?.name || ""}`
                            : subCategories.find(s => s.id === selectedSubCatId)?.name}
                    </h2>
                    <span className="text-xs text-muted-foreground">{products.length} products</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {loadingProducts ? (
                        Array.from({ length: 4 }).map((_, i) => <SkeletonProduct key={i} />)
                    ) : products.length > 0 ? (
                        products.map((product) => (
                            <ProductCard key={product.id} product={product} />
                        ))
                    ) : (
                        <div className="col-span-full py-20 text-center">
                            <p className="text-muted-foreground">No products found in this category.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function CategoryProductsPage() {
    return (
        <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>}>
            <CategoryProductsContent />
        </Suspense>
    );
}
